import React, { useContext, useState } from 'react'

import { ModalActionTypes, SelectionList } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { format } from 'date-fns'
import { compose } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import {
  ActionType,
  Currency,
  PageName,
  RecurringTransferAccountType,
  RecurringTransferDirection,
  RecurringTransferFundingSourceType,
  ScheduleRecurringTransferInput,
} from 'generated/types'
import { useFundingInitiatedPolling } from 'pages/Funding/Checking/useFundingInitiatedPolling'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { PromptProps, withPrompt } from 'utils/prompts'

import { useCheckingTransferTracking } from './events'
import scheduleRecurringTransferMutation from './gql/schedule-recurring-transfer-mutation.gql'
import { Content, Heading, NextButton, OneTimeDepositButton, SubHeading } from './styled-components'
import { useTransferAccounts } from './useTransferAccounts'
import { getDayOfWeek, getNextRecurringDate, getRecurrenceRule } from './utils'

type RecurringDepositScreenProps = WithFlowRouterProps & PromptProps

const DEFAULT_DEPOSIT_AMOUNT = '50'
const depositOptions = ['250', '100', '50', '25', '10'].map(n => ({
  label: `$${n}/weekly`,
  value: n,
}))

const enhance = compose<RecurringDepositScreenProps, {}>(withFlowRouter, withPrompt)

export const RecurringDeposit = enhance(({ route, promptUser }) => {
  const track = useCheckingTransferTracking('recurring')
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { getRemoteString } = useRemoteStrings()
  const { startPolling, isPolling } = useFundingInitiatedPolling(() => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.RecurringDeposit, ActionType.Continue)
      : route({ from: FlowRoute.RecurringDeposit, action: 'continue' })
  })

  const [scheduleRecurringTransfer, { loading }] = useMutation<
    any,
    { input: ScheduleRecurringTransferInput }
  >(scheduleRecurringTransferMutation, { onCompleted: startPolling })

  const {
    checkingAccountId,
    externalFundingSourceId,
    loading: loadingTransferAccounts,
  } = useTransferAccounts()

  const [selectedAmount, setSelectedAmount] = useState(DEFAULT_DEPOSIT_AMOUNT)

  const frequencyValue = {
    day: getDayOfWeek().toString(),
    frequency: 'weekly',
    interval: 1,
  }

  const onSetDeposit = () => {
    track.ctaTapped(selectedAmount)
    const nextTransferDate = format(
      getNextRecurringDate(new Date(), frequencyValue.frequency),
      'MMMM DD, YYYY',
    )

    track.confirmModalViewed()
    promptUser({
      image: null,
      title: getRemoteString({
        key: 'checking.card_order.fund_account.recurring.alert.title',
      }),
      message: getRemoteString({
        key: 'checking.card_order.fund_account.recurring.alert.body.variable',
        variables: [`$${selectedAmount}`, nextTransferDate],
      }),
      actions: [
        {
          type: ModalActionTypes.secondary,
          label: getRemoteString({
            key: 'checking.card_order.fund_account.alert.cta.secondary',
          }),
        },
        {
          type: ModalActionTypes.primary,
          label: getRemoteString({
            key: 'checking.card_order.fund_account.alert.cta.primary',
          }),
        },
      ],
    }).then(result => {
      if (result.index === 1) {
        track.confirmModalCtaTapped()
        scheduleRecurringTransfer({
          variables: {
            input: {
              recurrenceRule: getRecurrenceRule(frequencyValue),
              transferEvent: {
                direction: RecurringTransferDirection.Deposit,
                targetAccount: {
                  id: checkingAccountId,
                  type: RecurringTransferAccountType.Spend,
                },
                fundingSource: {
                  id: externalFundingSourceId,
                  type: RecurringTransferFundingSourceType.External,
                },
                amount: { currency: Currency.Usd, value: parseFloat(selectedAmount) },
              },
            },
          },
        })
      } else if (result.index === 0) {
        track.confirmModalCancelTapped()
      }
    })
  }

  const goToOneTimeTransfer = () => {
    track.secondaryCtaTapped()
    isDynamicFunnelEnabled
      ? routeToPage(PageName.RecurringDeposit, ActionType.Switch)
      : route({ from: FlowRoute.RecurringDeposit, action: 'switch' })
  }

  if (loading || loadingTransferAccounts || isPolling) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav />
      <Layout>
        <Content>
          <Heading>
            {getRemoteString({
              key: 'checking.card_order.fund_account.recurring.header',
            })}
          </Heading>
          <SubHeading>
            {getRemoteString({
              key: 'checking.card_order.fund_account.recurring.body',
            })}
          </SubHeading>
          <SelectionList
            className={'selection-list'}
            value={selectedAmount}
            options={depositOptions}
            onSelect={option => {
              setSelectedAmount(option.value)
            }}
          />
        </Content>
        <Separator />
        <NextButton onPress={onSetDeposit}>
          {getRemoteString({
            key: 'checking.card_order.fund_account.recurring.cta.primary',
          })}
        </NextButton>
        <OneTimeDepositButton onPress={() => goToOneTimeTransfer()} styleType={'flat'} secondary>
          {getRemoteString({
            key: 'checking.card_order.fund_account.recurring.cta.secondary',
          })}
        </OneTimeDepositButton>
      </Layout>
    </>
  )
})
